import type { ColumnPassThroughAttributes, ColumnPassThroughOptions } from 'primevue/column';
import type { DataTablePassThroughAttributes, DataTablePassThroughMethodOptions, DataTablePassThroughOptions } from 'primevue/datatable';
import type Popover from 'primevue/popover';
import type { ComputedRef, Ref } from 'vue';
import { useTranslation } from 'i18next-vue';
import {
  drop,
  find,
  isEqual,
  isNumber,
  sumBy,
  uniq,
} from 'lodash-es';
import {
  computed,
  nextTick,
  ref,
  watch,
} from 'vue';

import type { ISmartFilterColumn } from '@modules/smart-filters/types';
import {
  assert,
  isArrayLength,
  isEmpty,
  isNullOrUndefined,
  isObject,
  isUndefined,
} from '@utils/guards';
import { watchImmediate } from '@vueuse/core';

import type {
  ITableBaseRecordType,
  ITableEmits,
  ITableProps,
} from '../components/VTable/types';
import type {
  IDataTestCells,
  ITableDataResponseColumn,
  ITableState,
  ITableStateUpdateAction,
  ITableTotalRowData,
} from '../types';
import { QA_TABLES } from '../constants';
import { ensureList } from '../utils';

export interface TableComposable<TColumn> {
  selection: Ref<ITableBaseRecordType[]>;
  isMultiselectEnabled: ComputedRef<boolean>;
  selectionMode: ComputedRef<'multiple' | undefined>;
  columns: ComputedRef<ITableDataResponseColumn[] | undefined>;
  list: ComputedRef<TColumn[] | undefined>;
  totalColumns: ComputedRef<ITableDataResponseColumn[]>;
  subTotalData: ComputedRef<ITableTotalRowData | undefined>;
  dataIsEmpty: ComputedRef<boolean>;
  selectionIdsOnPage: ComputedRef<number[]>;
  defaultCellPt: ColumnPassThroughOptions;
  filterColumnOptions: ComputedRef<Record<string, Omit<ISmartFilterColumn, 'id'>>>;
  isSearchEnabled: ComputedRef<boolean | undefined>;
  transitionDelayMS: number;
  transitionMaxWaitMS: number;
  isIdColumnExistedInList: Ref<boolean>;
  overlayPanel: Ref<InstanceType<typeof Popover> | null>;
  isVisible: Ref<boolean>;
  tableCellContent: Ref<string>;
  tablePt: ComputedRef<DataTablePassThroughOptions>;
  multiselectColumnPt: ComputedRef<ColumnPassThroughOptions>;
  multiselectActionsColumnPt: ComputedRef<ColumnPassThroughOptions>;
  dataTestCells: ComputedRef<IDataTestCells>;
  handleMultiselectAction(actionId: string): void;
  handlePagination(page: number): void;
  handleSearch(keyword: string): void;
  handleOrder(columnName: string): void;
  applyFilter(filter_set_id?: number): void;
  clearFilter(): void;
  onSelect(): Promise<void>;
  onSelectMobileRow(id: number, isSelected?: boolean): void;
  onSelectedAllMobileRows(isSelected?: boolean): void;
  getTotalCellPt(columnName: string): ColumnPassThroughOptions;
  toggleOverlay(event: Event): void;
}

export default function useTable<TColumn>(
  data: Ref<ITableProps<TColumn>['data']>,
  state: Ref<ITableProps<TColumn>['state']>,
  multiselect: Ref<ITableProps<TColumn>['multiselect']>,
  selectedIds: Ref<ITableProps<TColumn>['selectedIds']>,
  columnsOptions: Ref<ITableProps<TColumn>['columnsOptions']>,
  subtotalEnabled: Ref<ITableProps<TColumn>['subtotalEnabled']>,
  isFetching: Ref<ITableProps<TColumn>['isFetching']>,
  emit: ITableEmits,
): TableComposable<TColumn> {
  const transitionDelayMS = 350;
  const transitionMaxWaitMS = 1000;
  const multiselectCellClass = '!w-[35px] !py-0 !px-[1px]';
  const multiselectContentClass = '!w-[35px]';

  const selection = ref<ITableBaseRecordType[]>([]);
  const isVisible = ref(false);
  const overlayPanel = ref<InstanceType<typeof Popover> | null>(null);
  const tableCellContent = ref('');

  const innerSelectionIds = computed<number[]>(() => selection.value.map((item) => item.id));
  const isMultiselectEnabled = computed<boolean>(() => Object.keys(multiselect.value ?? {}).length > 0);
  const { t } = useTranslation();

  const additionalCalculateColumns = {
    ctr: (selectedOnPage: TColumn[]): number | null => calculateSeparateColumns(sumBy(selectedOnPage, 'clicks_count'), sumBy(selectedOnPage, 'views_count')),
    cr: (selectedOnPage: TColumn[]): number | null => calculateSeparateColumns(sumBy(selectedOnPage, 'unique_clicks_count'), sumBy(selectedOnPage, 'clicks_count')),
    vr: (selectedOnPage: TColumn[]): number | null => calculateSeparateColumns(sumBy(selectedOnPage, 'unique_views_count'), sumBy(selectedOnPage, 'views_count')),
  };

  const columns = computed<ITableDataResponseColumn[] | undefined>(() => data.value?.columns_for_display_data.map((column) => ({
    ...column,
    label: t(`${data.value?.locale_key}.${column.name}`, { defaultValue: column.name }),
    columnOptions: {
      ...(columnsOptions.value?.[column.name] ?? {}),
    },
  })));

  const list = computed<TColumn[] | undefined>(() => {
    if (data.value?.list) {
      if (Array.isArray(data.value.list)) {
        return data.value.list;
      }

      return data.value.list.data.map((item) => item.attributes);
    }

    return undefined;
  });

  const isIdColumnExistedInList = computed(() => !!find(list.value, (item) => !!(item as ITableBaseRecordType).id));

  const totalColumns = computed<ITableDataResponseColumn[]>(() => drop(columns.value));

  const subtotalAvailable = computed(() => !!data.value?.sum_columns);

  const subTotalData = computed<ITableTotalRowData | undefined>(() => {
    const result: ITableTotalRowData = {};
    const sumColumns = data.value?.sum_columns;

    const selectedOnPage = list.value?.filter((listItem) => (
      !!find(selection.value, (item) => {
        const listItemRecord = listItem as ITableBaseRecordType;
        return item.id && listItemRecord.id ? item.id === listItemRecord.id : isEqual(listItemRecord, item);
      })
    )) ?? [];

    if (!sumColumns || selectedOnPage.length < 2 || (selectedOnPage.length === list.value?.length && data.value?.total_pages === 1)) {
      return undefined;
    }

    for (const columnName of Object.keys(sumColumns)) {
      result[columnName] = isNumber(sumColumns[columnName]) ? sumBy(selectedOnPage, columnName) : null;
    }

    for (const [name, calculate] of Object.entries(additionalCalculateColumns)) {
      if (!isNullOrUndefined(result[name])) {
        result[name] = calculate(selectedOnPage);
      }
    }

    return result;
  });

  const dataIsEmpty = computed<boolean>(() => isEmpty(list.value) || isArrayLength(list.value, 0));

  const selectionIdsOnPage = computed<number[]>(() => {
    if (!(list.value && innerSelectionIds.value))
      return [];

    const listIds = getListIds();

    return innerSelectionIds.value.filter((id) => listIds.includes(id)).sort((a, b) => a - b);
  });

  const isSearchEnabled = computed(() => columns.value?.some(({ match }) => match));

  const selectionMode = computed(() => {
    if (subtotalAvailable.value) {
      return subtotalEnabled.value ? 'multiple' : undefined;
    }

    return 'multiple';
  });

  function emitStateUpdate(tableState: ITableState, action?: ITableStateUpdateAction): void {
    emit('state-update', tableState, action);
  }

  function handleMultiselectAction(actionId: string): void {
    emit('multiselect-action', {
      actionId,
      selection: selectionIdsOnPage.value,
    });
  }

  function handlePagination(page: number): void {
    emitStateUpdate({
      ...state.value,
      page,
    });
  }

  function handleSearch(keyword: string): void {
    emitStateUpdate({
      ...state.value,
      page: 1,
      search: {
        keyword,
      },
    });
  }

  function handleOrder(columnName: string): void {
    assert(!isUndefined(state.value));

    let newOrder = state.value.order;
    if (newOrder?.[columnName]) {
      newOrder[columnName] = newOrder[columnName] === 'desc' ? 'asc' : undefined;
    } else {
      newOrder = { [columnName]: 'desc' };
    }

    emitStateUpdate({
      ...state.value,
      order: newOrder,
    });
  }

  function applyFilter(filter_set_id?: number): void {
    emitStateUpdate({
      ...state.value,
      page: 1,
      filter_set_id,
    }, 'applyFilter');
  }

  function clearFilter(): void {
    emitStateUpdate({
      ...state.value,
      page: 1,
      filter_set_id: undefined,
    });
  }

  function resetSelection(): void {
    selection.value = [];
  }

  watchImmediate(selectedIds, (newValue) => {
    selection.value = newValue?.map((id) => ({ id })) ?? [];
  });

  watchImmediate(subtotalEnabled, (newValue, oldValue) => {
    // reset selection if subtotal has been disabled
    if (oldValue && !newValue) {
      resetSelection();
    }
  });

  function bodyRowPt({ context }: DataTablePassThroughMethodOptions): DataTablePassThroughAttributes {
    return {
      'class': [{ 'bg-primary-100 hover:bg-primary-50': context.selected }],
      'data-test-el': QA_TABLES.bodyRow,
      'data-test-attr-row-index': context.index,
    };
  }

  async function onSelect(): Promise<void> {
    await nextTick();
    emit('selections-change', innerSelectionIds.value);
  }

  const defaultCellPt: ColumnPassThroughOptions = {
    headerCell: {
      class: 'h-[50px] !px-6 !py-2',
    },
    bodyCell: {
      class: 'max-w-[450px] h-[50px] !px-6 !py-2 text-ellipsis',
    },
  };

  const tablePt = computed<DataTablePassThroughOptions>(() => {
    return {
      header: { class: '!border-none p-0 rounded-t-xl' },
      headerRow: {
        'data-test-el': QA_TABLES.headRow,
      },
      tableContainer: { class: [
        'opacity-0 transition-opacity duration-300',
        { '!opacity-100': isVisible.value },
        'min-h-[300px]',
        { static: data.value && dataIsEmpty.value },
      ] },
      emptyMessage: { class: [
        'h-[300px]',
        { 'h-[250px]': data.value && dataIsEmpty.value },
      ] },
      bodyRow: bodyRowPt,
    };
  });

  const multiselectColumnPt = computed<ColumnPassThroughOptions>(() => ({
    ...defaultCellPt,
    bodyCell: {
      ...(isObject(defaultCellPt?.bodyCell) ? defaultCellPt?.bodyCell : {}),
      'data-test-el': QA_TABLES.bodyRowCheckbox,
    },
    headerCell() {
      return {
        ...(isObject(defaultCellPt?.headerCell) ? defaultCellPt?.headerCell : {}),
        'data-test-el': QA_TABLES.headCheckboxAll,
      };
    },
  }));

  const multiselectActionsColumnPt = computed<ColumnPassThroughOptions>(() => ({
    headerCell: {
      class: multiselectCellClass,
    },
    bodyCell: {
      class: multiselectCellClass,
    },
    footerCell: {
      class: multiselectCellClass,
    },
    headerContent: {
      class: multiselectContentClass,
    },
    bodyCellContent: {
      class: multiselectContentClass,
    },
  }));

  const dataTestCells = computed<IDataTestCells>(() => {
    const result: IDataTestCells = {
      head: {},
      body: {},
    };

    if (!columns.value) {
      return result;
    }

    for (const column of columns.value) {
      result.head[column.name] = {
        el: QA_TABLES.headCell,
        columnName: column.name,
      };
      result.body[column.name] = {
        el: QA_TABLES.bodyRowCell,
        columnName: column.name,
      };
    }

    return result;
  });

  const filterColumnOptions = computed<Record<string, Omit<ISmartFilterColumn, 'id'>>>(() => (
    Object.entries(columnsOptions.value ?? {}).reduce((accumulator, [key, option]) => {
      if (option.type) {
        Object.assign(accumulator, {
          [key]: {
            type: option.type,
            disableCheckboxFilter: option.disableCheckboxFilter,
            ...option.localization,
          },
        });
      }

      return accumulator;
    }, {} as Record<string, Omit<ISmartFilterColumn, 'id'>>)
  ));

  function getTotalCellPt(columnName: string): ColumnPassThroughOptions {
    return {
      footerCell: {
        'class': `${(defaultCellPt.bodyCell as ColumnPassThroughAttributes)?.class || ''} bg-primary-100`,
        'data-test-el': QA_TABLES.bodyRowCell,
        'data-test-attr-column-name': columnName,
      },
    };
  }

  function getListIds(): number[] {
    return ensureList<ITableBaseRecordType>(list.value)?.map(({ id }) => id) || [];
  }

  function onSelectMobileRow(id: number, isSelected?: boolean): void {
    if (isSelected) {
      selection.value.push({ id });
    } else {
      selection.value = selection.value.filter((item) => item.id !== id);
    }
  }

  function onSelectedAllMobileRows(isSelected?: boolean): void {
    if (isSelected) {
      selection.value = uniq([
        ...selection.value.map(({ id }) => id),
        ...getListIds(),
      ]).map((id) => ({ id }));
    } else {
      selection.value = selection.value.filter((item) => !selectionIdsOnPage.value.includes(item.id));
    }
  }

  function calculateSeparateColumns(value1?: number, value2?: number): number | null {
    if (isNullOrUndefined(value1) || isNullOrUndefined(value2))
      return null;
    if (value2 === 0 && value1 === 0)
      return 0;
    if (value2 === 0 && value1 > 0)
      return 100;
    return Math.round((value1 / value2) * 100);
  }

  function isOverlayVisible<T extends HTMLElement, P extends HTMLElement>(target?: T, parent?: P): boolean {
    if (!target || !parent)
      return false;

    const parentStyle = getComputedStyle(parent);
    const parentWidth = parent.clientWidth - Number.parseFloat(parentStyle.paddingLeft) - Number.parseFloat(parentStyle.paddingRight);
    const targetWidth = target.offsetWidth;

    return targetWidth > parentWidth;
  }

  function toggleOverlay(event: Event): void {
    const parent = (event.target as HTMLElement).closest('td');
    const child = parent?.querySelector('[data-id="table-body-row-cell"]') as HTMLElement | null;

    if (child && parent) {
      if (isOverlayVisible(child, parent)) {
        tableCellContent.value = child.outerHTML;
        event.stopPropagation();
        event.preventDefault();
        overlayPanel.value?.toggle(event, parent);
      } else {
        overlayPanel.value?.hide();
      }
    } else {
      overlayPanel.value?.hide();
    }
  }

  watch(data, (newData) => {
    if (isFetching.value)
      return;

    // decrease page index if no data on current page
    if (state.value?.page && state.value?.page > 1
      && newData?.total_pages
      && newData.total_pages < state.value.page) {
      handlePagination(state.value.page - 1);
    }
  });

  return {
    selection,
    isMultiselectEnabled,
    selectionMode,
    columns,
    list,
    totalColumns,
    subTotalData,
    dataIsEmpty,
    selectionIdsOnPage,
    defaultCellPt,
    filterColumnOptions,
    isSearchEnabled,
    transitionDelayMS,
    transitionMaxWaitMS,
    isIdColumnExistedInList,
    overlayPanel,
    isVisible,
    tableCellContent,
    tablePt,
    multiselectColumnPt,
    multiselectActionsColumnPt,
    dataTestCells,
    handleMultiselectAction,
    handlePagination,
    handleSearch,
    handleOrder,
    applyFilter,
    clearFilter,
    onSelect,
    onSelectMobileRow,
    onSelectedAllMobileRows,
    getTotalCellPt,
    toggleOverlay,
  };
}
